import { useEffect, useRef } from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { MessageChat } from "../types";
import { APP_ENV, BACKEND_URL, REVERB_APP_KEY, REVERB_HOST, REVERB_PORT, REVERB_SCHEME } from "../global-components/src/utils/vars";

const isProduction = APP_ENV === "production";

const DEFAULT_BACKEND_URL = APP_ENV === "production"
    ? "https://api.pacoca.net"
    : "http://192.168.100.33:8080";

function nonEmpty(value: unknown, fallback: string): string {
    const normalized = String(value ?? "").trim();
    return normalized || fallback;
}

export function getBroadcastingAuthUrl(baseUrl: string): string {
    const normalized = nonEmpty(baseUrl, DEFAULT_BACKEND_URL)
        .replace(/\/$/, "")
        .replace(/\/api$/, "");

    return `${normalized}/broadcasting/auth`;
}

export function createEcho(token: string): Echo<"reverb"> {
    (window as any).Pusher = Pusher;

    const backendUrl = nonEmpty(BACKEND_URL, DEFAULT_BACKEND_URL);
    const reverbHost = nonEmpty(
        REVERB_HOST,
        isProduction ? "reverb.pacoca.net" : "192.168.100.33",
    );
    const reverbScheme = nonEmpty(REVERB_SCHEME, isProduction ? "https" : "http").toLowerCase();
    const reverbPort = REVERB_PORT > 0 ? REVERB_PORT : (isProduction ? 443 : 9001);
    const reverbKey = nonEmpty(REVERB_APP_KEY, "s3w3thzezulgp5g0e5bs");
    const apiKey = String(process.env.REACT_APP_PACOCA_X_API_KEY ?? "").trim();

    return new Echo({
        broadcaster: "reverb",
        // Mantém a implementação explícita para funcionar também quando o
        // cliente for carregado dentro de uma WebView.
        Pusher,
        key:      reverbKey,
        wsHost:   reverbHost,
        wsPort:   reverbPort,
        wssPort:  reverbPort,
        forceTLS: reverbScheme === "https",
        // Pusher chooses the secure transport when forceTLS is enabled. Keeping
        // both transports enabled also makes the same bundle work with local
        // Reverb (ws) and behind the production TLS proxy (wss).
        enabledTransports: ["ws", "wss"],
        authEndpoint: getBroadcastingAuthUrl(backendUrl),
        auth: {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: "application/json",
                ...(apiKey ? { "X-Api-Key": apiKey } : {}),
            },
        },
        // Limit reconnect attempts so a missing dev server doesn't spam the console
        pongTimeout: 6000,
        unavailableTimeout: 10000,
        maxReconnectionAttempts: isProduction ? undefined : 3,
    } as any);
}

function parseMessageEvent(payload: unknown): { chatId: number; message: MessageChat } | null {
    let value: any = payload;

    if (typeof value === "string") {
        try {
            value = JSON.parse(value);
        } catch {
            return null;
        }
    }

    // Some proxies/Pusher adapters expose the event body under `data`.
    if (typeof value?.data === "string") return parseMessageEvent(value.data);
    if (value?.data && typeof value.data === "object") value = value.data;

    const message = value?.message ?? value?.messageData;
    const chatId = Number(value?.chat_id ?? value?.chatId ?? message?.chat_id);

    if (!message || !Number.isFinite(chatId)) return null;

    const nickname = value?.nickname;
    if (nickname && message.user) {
        message.user = {
            ...message.user,
            original_name: message.user.name,
            nickname,
            name: nickname,
        };
    }

    return { chatId, message: message as MessageChat };
}

interface UseChatEchoOptions {
    /** The logged-in user's id. Used to subscribe to the per-user channel. */
    userId: number | undefined;
    token: string | undefined | null;
    /** Called whenever a message arrives for any chat. */
    onMessage: (chatId: number, message: MessageChat) => void;
    /** Called when a participant advances their read marker. */
    onRead?: (payload: {
        chat_id: number;
        reader_id: number;
        message_id: number;
        read_at?: string;
        is_read?: boolean;
        read_by_count?: number;
        read_by_total?: number;
    }) => void;
    /** Called when a participant starts or stops typing. */
    onTyping?: (payload: { chat_id: number; user_id: number; user_name: string; user_img_account?: string | null; is_typing: boolean }) => void;
    /** Called when a participant starts or stops recording audio. */
    onRecording?: (payload: { chat_id: number; user_id: number; user_name: string; user_img_account?: string | null; is_recording: boolean }) => void;
}

/**
 * Connects to Laravel Reverb (via Echo) and listens on the private
 * `chat-user.{userId}` channel.  Disconnects automatically on unmount or
 * when userId/token changes.
 */
export function useChatEcho({ userId, token, onMessage, onRead, onTyping, onRecording }: UseChatEchoOptions) {
    const echoRef = useRef<Echo<"reverb"> | null>(null);
    const onMessageRef = useRef(onMessage);
    const onReadRef = useRef(onRead);
    const onTypingRef = useRef(onTyping);
    const onRecordingRef = useRef(onRecording);

    // Assign synchronously during render so the WS listener always calls the
    // latest callback — no async useEffect delay between a state change and the
    // next incoming event.
    onMessageRef.current = onMessage;
    onReadRef.current = onRead;
    onTypingRef.current = onTyping;
    onRecordingRef.current = onRecording;

    useEffect(() => {
        if (!userId || !token) return;

        const echo = createEcho(token);
        echoRef.current = echo;

        echo
            .private(`chat-user.${userId}`)
            .listen(".MessageSent", (data: unknown) => {
                const event = parseMessageEvent(data);
                if (event) onMessageRef.current(event.chatId, event.message);
            })
            .listen(".ChatReadUpdated", (data: {
                chat_id: number;
                reader_id: number;
                message_id: number;
                read_at?: string;
                is_read?: boolean;
                read_by_count?: number;
                read_by_total?: number;
            }) => {
                onReadRef.current?.(data);
            })
            .listen(".ChatTypingUpdated", (data: { chat_id: number; user_id: number; user_name: string; user_img_account?: string | null; is_typing: boolean }) => {
                onTypingRef.current?.(data);
            })
            .listen(".ChatAudioRecordingUpdated", (data: { chat_id: number; user_id: number; user_name: string; user_img_account?: string | null; is_recording: boolean }) => {
                onRecordingRef.current?.(data);
            });

        return () => {
            echo.leave(`chat-user.${userId}`);
            echo.disconnect();
            echoRef.current = null;
        };
    }, [userId, token]);
}
